import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * A square, icon-only control. Appearance is `.icon-btn` in `styles/ui.css`.
 *
 * Separate from `Button` rather than a `size="icon"` on it, because the two
 * disagree about the thing `Button` is built around: `Button` is a labelled
 * control with horizontal padding and a text child, and every one of its
 * variants carries a background. This is a bare hit area that only paints on
 * hover, and its accessible name has to come from a prop because there is no
 * text to read.
 *
 * **`label` is required and there is no way to omit it.** An icon button with no
 * accessible name is announced as "button" and nothing else, which is the single
 * most common way a keyboard-only user loses a control entirely. Making it
 * required means that cannot be forgotten rather than merely discouraged.
 *
 * `as="span"` exists for the one real case: `Menu` renders its own `<button>`
 * around whatever trigger it is given, and a button inside a button is invalid
 * HTML that browsers resolve by dropping one of them. In that mode the label is
 * rendered as visually-hidden text instead of `aria-label`, because `aria-label`
 * on a plain `<span>` has no role to attach to and is ignored.
 */

type Tone = "default" | "panel" | "danger";
type Size = "sm" | "md" | "lg";

export interface IconButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "className" | "children"
> {
  /** The accessible name. Not optional - see the note above. */
  label: string;
  tone?: Tone;
  size?: Size;
  /** Render as a `<span>` for use inside another button, e.g. a `Menu` trigger. */
  as?: "button" | "span";
  children: React.ReactNode;
  className?: string;
}

export function IconButton({
  label,
  tone = "default",
  size = "md",
  as = "button",
  children,
  className,
  type = "button",
  ...rest
}: IconButtonProps) {
  const classes = cn("icon-btn", `icon-btn--${tone}`, `icon-btn--${size}`, className);

  if (as === "span") {
    return (
      <span className={classes}>
        {children}
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  return (
    <button type={type} aria-label={label} title={label} className={classes} {...rest}>
      {children}
    </button>
  );
}
